import { v4 as uuid4 } from 'uuid';
import { vttTimeToMmSs } from '../time-utils';
import { MediaType, EasingIn, EasingOut } from '../animation-constants';
export function useVttUpload() {
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    function sanitizeText(text) {
        if (!text || typeof text !== 'string') {
            return '';
        }
        return text
            .replace(/<[^>]*>/g, '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#x27;')
            .replace(/\//g, '&#x2F;')
            .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
            .trim();
    }
    function parseVttContent(content, options = {}) {
        const { mode = 'import' } = options;
        if (!content || typeof content !== 'string') {
            throw new Error('Invalid file content');
        }
        const trimmedContent = content.trim();
        if (!trimmedContent.startsWith('WEBVTT')) {
            throw new Error('Invalid VTT file: Missing WEBVTT header');
        }
        const cues = [];
        const lines = content.split('\n');
        let i = 0;
        let lineNumber = 0;
        try {
            while (i < lines.length) {
                const line = lines[i].trim();
                if (line.startsWith('NOTE') || line.startsWith('STYLE')) {
                    i++;
                    while (i < lines.length && lines[i].trim() !== '') {
                        i++;
                    }
                    i++;
                    continue;
                }
                if (line.includes('-->')) {
                    break;
                }
                i++;
            }
            // Parse cues
            while (i < lines.length) {
                const line = lines[i].trim();
                lineNumber = i + 1;
                if (line === '') {
                    i++;
                    continue;
                }
                if (!line.includes('-->') && i + 1 < lines.length && lines[i + 1].includes('-->')) {
                    i++;
                    continue;
                }
                if (line.includes('-->')) {
                    const timestampMatch = line.match(/(\d{2}:\d{2}:\d{2}\.\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2}\.\d{1,3})/);
                    if (!timestampMatch) {
                        if (mode === 'import') {
                            console.warn(`Skipping malformed timestamp at line ${lineNumber}: ${line}`);
                        }
                        i++;
                        continue;
                    }
                    try {
                        const startTime = vttTimeToMmSs(timestampMatch[1]);
                        const endTime = vttTimeToMmSs(timestampMatch[2]);
                        i++;
                        const textLines = [];
                        while (i < lines.length && lines[i].trim() !== '' && !lines[i].includes('-->')) {
                            const textLine = lines[i].trim();
                            if (!textLine.startsWith('NOTE') && !textLine.startsWith('STYLE')) {
                                textLines.push(textLine);
                            }
                            i++;
                        }
                        const text = textLines.join(' ').trim();
                        const sanitizedText = sanitizeText(text);
                        if (sanitizedText) {
                            if (mode === 'import') {
                                cues.push({
                                    id: uuid4(),
                                    mediaType: MediaType.TEXT,
                                    mediaUrl: '',
                                    textContent: sanitizedText,
                                    startTime,
                                    endTime,
                                    untilEnd: false,
                                    groupId: '',
                                    easingIn: EasingIn.NONE,
                                    easingOut: EasingOut.NONE
                                });
                            }
                            else {
                                cues.push({
                                    id: uuid4(),
                                    textContent: sanitizedText,
                                    startTime,
                                    endTime,
                                    easingIn: EasingIn.NONE,
                                    easingOut: EasingOut.NONE
                                });
                            }
                        }
                    }
                    catch (err) {
                        if (mode === 'import') {
                            console.warn(`Error parsing cue at line ${lineNumber}:`, err.message);
                        }
                        i++;
                    }
                }
                else {
                    i++;
                }
            }
            if (cues.length === 0) {
                throw new Error('No valid cues found in the VTT file');
            }
            return cues;
        }
        catch (error) {
            if (error.message.includes('No valid cues found') || error.message.includes('Invalid VTT file')) {
                throw error;
            }
            throw new Error(`Error parsing VTT file at line ${lineNumber}: ${error.message}`);
        }
    }
    function validateFile(file) {
        if (!file) {
            throw new Error('No file selected');
        }
        if (file.size > MAX_FILE_SIZE) {
            throw new Error('File size exceeds 5MB limit. Please upload a smaller VTT file.');
        }
        if (!file.name.toLowerCase().endsWith('.vtt')) {
            throw new Error('Invalid file type. Please upload a .vtt file.');
        }
        return true;
    }
    function readFileAsText(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                resolve(e.target?.result);
            };
            reader.onerror = () => {
                reject(new Error('Failed to read the file. Please try again.'));
            };
            reader.readAsText(file);
        });
    }
    function uploadVttFile(options = {}) {
        return new Promise((resolve, reject) => {
            const fileInput = document.createElement('input');
            fileInput.type = 'file';
            fileInput.accept = '.vtt';
            fileInput.onchange = async (e) => {
                const file = e.target?.files?.[0];
                try {
                    validateFile(file);
                    const content = await readFileAsText(file);
                    const cues = parseVttContent(content, options);
                    resolve({ cues, fileName: file.name });
                }
                catch (error) {
                    reject(error);
                }
            };
            fileInput.oncancel = () => {
                reject(new Error('File selection cancelled'));
            };
            fileInput.click();
        });
    }
    async function parseVttFile(file, options = {}) {
        validateFile(file);
        const content = await readFileAsText(file);
        return parseVttContent(content, options);
    }
    return {
        uploadVttFile,
        parseVttFile,
        parseVttContent,
        validateFile,
        sanitizeText
    };
}
